import { Check, Copy } from 'lucide-react'
import { useState } from 'react'
import { Tooltip, TooltipContent, TooltipTrigger } from '#components/tooltip'
import { cn } from '#lib/utils'

/**
 * Monospace id with a copy button. Clicking the button copies the id to the
 * clipboard, swaps to a check icon, and shows a "Copiato!" tooltip for a moment.
 * Language-agnostic controls (icon-only); the tooltip text is Italian.
 */
function IdCell(props: { id: string | number; className?: string }) {
	const [copied, setCopied] = useState(false)
	const [open, setOpen] = useState(false)
	const value = String(props.id)

	const copy = async () => {
		try {
			await navigator.clipboard.writeText(value)
			setCopied(true)
			setOpen(true)
			setTimeout(() => {
				setCopied(false)
				setOpen(false)
			}, 1500)
		} catch {
			// clipboard unavailable (insecure context) — no-op
		}
	}

	return (
		<span
			className={cn('flex min-w-0 items-center gap-1', props.className)}
		>
			<span className='truncate font-mono text-muted-foreground text-xs'>
				{value}
			</span>
			<Tooltip onOpenChange={setOpen} open={open}>
				<TooltipTrigger
					aria-label={`Copia ${value}`}
					className='shrink-0 rounded p-0.5 text-muted-foreground opacity-60 transition hover:bg-muted hover:opacity-100'
					onClick={copy}
				>
					{copied ? (
						<Check className='size-3.5 text-emerald-600' />
					) : (
						<Copy className='size-3.5' />
					)}
				</TooltipTrigger>
				<TooltipContent>{copied ? 'Copiato!' : 'Copia'}</TooltipContent>
			</Tooltip>
		</span>
	)
}

export { IdCell }
